import { z } from 'zod'
import { email, requiredString } from '../../primitives/string'
import { MESSAGES } from '../../utils/messages'

/**
 * Usuario de plataforma (staff/asesores) — fuente única de verdad para la
 * validación del API (nestjs-zod) y del formulario web (RHF + zodResolver).
 *
 * SEGURIDAD: acá solo viven los campos que un admin puede ESCRIBIR. Credenciales
 * y secretos (password, refreshToken, twoFactorAuthenticationSecret, config)
 * NUNCA forman parte de este contrato: el password inicial lo genera el backend
 * y viaja por correo; los cambios de credenciales van por los endpoints
 * self-service que verifican el password actual.
 */

export const TYPE_ACCOUNT = {
  ADMIN: 'admin',
  USER: 'user',
  ADVISOR: 'advisor'
} as const
export type TypeAccount = (typeof TYPE_ACCOUNT)[keyof typeof TYPE_ACCOUNT]

const objectIdString = z
  .string({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
  .regex(/^[a-f0-9]{24}$/i, 'Identificador inválido')

export const CreateUserInputSchema = z.object({
  fullname: requiredString(),
  email: email().toLowerCase(),
  // Username legible/único: identificador amigable en URLs y filtros.
  username: z
    .string({ required_error: MESSAGES.REQUIRED })
    .min(1, MESSAGES.REQUIRED)
    .regex(/^[a-zA-Z0-9]+$/, 'Solo letras y números, sin espacios')
    .toLowerCase(),
  typeAccount: z.nativeEnum(TYPE_ACCOUNT),
  roles: z.array(objectIdString).default([]),
  avatar: z.string().url('URL inválida').optional(),
  active: z.boolean().optional(),
  locked: z.boolean().optional()
})

export const UpdateUserInputSchema = CreateUserInputSchema.partial()

export type CreateUserInput = z.infer<typeof CreateUserInputSchema>
export type UpdateUserInput = z.infer<typeof UpdateUserInputSchema>
